/**
 * Server state for the casefile tab: a matter's runs, one run with its
 * decisions and blocking list, and the writes. The run view is polled only
 * while a rebuild is in flight, because the server merges the job's state
 * into the head. A 409 means the review was completed elsewhere: reload and
 * say so. A 422 carries the reasons the screen lists inline. Everything else
 * says what did not happen and offers Retry (docs/ux/shell.md).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError, api } from '@/app/api';
import type { DecisionInput, SortCorrection, SortReviewItem, SortRunHead, SortRunView } from '@/app/types';
import { detailKey, refreshAudit } from '../useMatter';

export const LOCKED_TEXT = 'This review was completed in another tab. Decisions are locked; nothing changed.';

export function runsKey(ref: string) {
  return ['matter', ref, 'sort'] as const;
}
export function runKey(ref: string, runId: string) {
  return ['matter', ref, 'sort', runId] as const;
}

const base = (ref: string) => `/api/matters/${encodeURIComponent(ref)}/sort`;
const runBase = (ref: string, runId: string) => `${base(ref)}/${encodeURIComponent(runId)}`;

export const pagesUrl = (ref: string, runId: string, from: number, to: number) => `${runBase(ref, runId)}/pages/${from}/${to}`;
export const sortedPdfUrl = (ref: string, runId: string, which: 'machine' | 'reviewed') => `${runBase(ref, runId)}/sorted.pdf?which=${which}`;
/** The bundle as received, one PDF in received order, read in the browser's own viewer. */
export const sourcePdfUrl = (ref: string, runId: string) => `${runBase(ref, runId)}/source.pdf`;
/** One document's pages, as received, for the sorted viewer; unlike the page-range endpoint it is not capped at a viewer window. */
export const documentPdfUrl = (ref: string, runId: string, from: number, to: number) => `${runBase(ref, runId)}/document.pdf?from=${from}&to=${to}`;

export function useSortRuns(ref: string) {
  return useQuery({
    queryKey: runsKey(ref),
    queryFn: () => api<{ runs: SortRunHead[] }>(base(ref)),
    select: (d) => d.runs,
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
    staleTime: 10_000,
  });
}

export const rebuilding = (head: SortRunHead | undefined): boolean => head?.rebuild?.state === 'queued' || head?.rebuild?.state === 'running';

export function useSortRun(ref: string, runId: string | null) {
  return useQuery({
    queryKey: runKey(ref, runId ?? ''),
    queryFn: () => api<SortRunView>(runBase(ref, runId!)),
    enabled: runId !== null,
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
    staleTime: 5_000,
    refetchInterval: (query) => (rebuilding(query.state.data?.head) ? 1_500 : false),
  });
}

/** The reasons a 422 carried, for the inline list; null when the error was something else. */
export function reasonsOf(err: unknown): string[] | null {
  if (!(err instanceof ApiError) || err.status !== 422 || !err.body || typeof err.body !== 'object') return null;
  const body = err.body as { reasons?: unknown; reason?: unknown };
  if (Array.isArray(body.reasons)) return body.reasons.filter((r): r is string => typeof r === 'string');
  if (typeof body.reason === 'string') return [body.reason];
  return null;
}

function failed(message: string, retry: () => void): void {
  toast.error(`${message} Nothing changed.`, { action: { label: 'Retry', onClick: retry } });
}

function useRunInvalidation(ref: string, runId: string) {
  const client = useQueryClient();
  return {
    client,
    /** After a lock: the run, the matter (its gate) and the audit are all reloaded. */
    reloadAll: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: runKey(ref, runId) }),
        client.invalidateQueries({ queryKey: runsKey(ref) }),
        client.invalidateQueries({ queryKey: detailKey(ref) }),
        refreshAudit(client, ref),
      ]);
    },
  };
}

export function useStartReview(ref: string, runId: string) {
  const { client, reloadAll } = useRunInvalidation(ref, runId);
  const mutation = useMutation({
    mutationFn: () => api<SortRunHead>(`${runBase(ref, runId)}/review/start`, { method: 'POST' }),
    onSuccess: (head) => {
      client.setQueryData<SortRunView>(runKey(ref, runId), (prev) => (prev ? { ...prev, head: { ...prev.head, ...head } } : prev));
      void refreshAudit(client, ref);
    },
    onError: async (err) => {
      if (err instanceof ApiError && err.status === 409) { await reloadAll(); toast.error(LOCKED_TEXT); return; }
      failed('Could not start the review.', () => mutation.mutate());
    },
  });
  return mutation;
}

export type DecideVars = { itemId: string; input: DecisionInput; label: string };

export function useDecide(ref: string, runId: string, callbacks: { onRefused?: (itemId: string, reasons: string[]) => void } = {}) {
  const { client, reloadAll } = useRunInvalidation(ref, runId);
  const mutation = useMutation({
    mutationFn: (vars: DecideVars) => api<SortReviewItem>(`${runBase(ref, runId)}/review/items/${encodeURIComponent(vars.itemId)}`, { method: 'PUT', json: vars.input }),
    onSuccess: (item) => {
      // The blocking list and the state are the server's; the item is patched in and the rest refetched.
      client.setQueryData<SortRunView>(runKey(ref, runId), (prev) => (prev ? { ...prev, items: prev.items.map((i) => (i.itemId === item.itemId ? item : i)) } : prev));
      void client.invalidateQueries({ queryKey: runKey(ref, runId) });
      void refreshAudit(client, ref);
    },
    onError: async (err, vars) => {
      if (err instanceof ApiError && err.status === 409) { await reloadAll(); toast.error(LOCKED_TEXT); return; }
      const reasons = reasonsOf(err);
      if (reasons) { callbacks.onRefused?.(vars.itemId, reasons); toast.error(`Could not save the decision: ${reasons[0]}. Nothing changed.`); return; }
      failed(`Could not save ${vars.label}.`, () => mutation.mutate(vars));
    },
  });
  return mutation;
}

export type AdditionalVars = { documentId: string; correction: SortCorrection; note?: string };

export function useAddAdditional(ref: string, runId: string, callbacks: { onRefused?: (reasons: string[]) => void } = {}) {
  const { client, reloadAll } = useRunInvalidation(ref, runId);
  const mutation = useMutation({
    mutationFn: (vars: AdditionalVars) => api<SortReviewItem>(`${runBase(ref, runId)}/review/additional`, { method: 'POST', json: vars }),
    onSuccess: (item) => {
      client.setQueryData<SortRunView>(runKey(ref, runId), (prev) => (prev ? { ...prev, items: [...prev.items, item] } : prev));
      void client.invalidateQueries({ queryKey: runKey(ref, runId) });
      void refreshAudit(client, ref);
    },
    onError: async (err, vars) => {
      if (err instanceof ApiError && err.status === 409) { await reloadAll(); toast.error(LOCKED_TEXT); return; }
      const reasons = reasonsOf(err);
      if (reasons) { callbacks.onRefused?.(reasons); toast.error(`Could not record the correction: ${reasons[0]}. Nothing changed.`); return; }
      failed('Could not record the correction.', () => mutation.mutate(vars));
    },
  });
  return mutation;
}

export function useCompleteReview(ref: string, runId: string, callbacks: { onRefused?: (reasons: string[]) => void; onCompleted?: () => void } = {}) {
  const { client, reloadAll } = useRunInvalidation(ref, runId);
  const mutation = useMutation({
    mutationFn: () => api<{ head: SortRunHead; jobId: string }>(`${runBase(ref, runId)}/review/complete`, { method: 'POST' }),
    onSuccess: (data) => {
      client.setQueryData<SortRunView>(runKey(ref, runId), (prev) => (prev ? { ...prev, head: { ...prev.head, ...data.head } } : prev));
      void client.invalidateQueries({ queryKey: runKey(ref, runId) });
      void client.invalidateQueries({ queryKey: runsKey(ref) });
      void refreshAudit(client, ref);
      toast.success('Review complete. Rebuilding the reviewed casefile.');
      callbacks.onCompleted?.();
    },
    onError: async (err) => {
      if (err instanceof ApiError && err.status === 409) { await reloadAll(); toast.error(LOCKED_TEXT); return; }
      const reasons = reasonsOf(err);
      if (reasons) { void client.invalidateQueries({ queryKey: runKey(ref, runId) }); callbacks.onRefused?.(reasons); toast.error('The review cannot be completed yet. Nothing changed.'); return; }
      failed('Could not complete the review.', () => mutation.mutate());
    },
  });
  return mutation;
}

export function useRebuildAgain(ref: string, runId: string) {
  const { client } = useRunInvalidation(ref, runId);
  const mutation = useMutation({
    mutationFn: () => api<{ jobId: string; state: string }>(`${runBase(ref, runId)}/review/rebuild`, { method: 'POST' }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: runKey(ref, runId) });
      toast.success('Rebuilding the reviewed casefile again.');
    },
    onError: (err) => {
      const reasons = reasonsOf(err);
      if (reasons) { toast.error(`Could not rebuild: ${reasons[0]}. Nothing changed.`); return; }
      failed('Could not start the rebuild.', () => mutation.mutate());
    },
  });
  return mutation;
}

/** Invalidate the matter record once a rebuild succeeds, so the header's gate re-renders from the server. */
export function useGateRefresh(ref: string, head: SortRunHead | undefined) {
  const client = useQueryClient();
  return () => {
    if (head?.rebuild?.state === 'succeeded') {
      void client.invalidateQueries({ queryKey: detailKey(ref) });
      void refreshAudit(client, ref);
    }
  };
}
